/*
 * Your Hall  ·  Satchel + Saved items renderer
 * assets/hoh-hall-saved.js
 *
 * v2.1 hub: the Satchel tab is the single home for the DM's
 * tools (Scene, Tavern, Company) and their saved instances. This
 * file owns:
 *
 *   - hohHallRenderSatchel(host)              top-level Satchel tab
 *   - hohHallRenderToolsPanel(host, opts)     the DM Tools tiles
 *   - hohShowHallToast(text, opts)            shared toast
 *
 * Jan (2026-09-27): the saved rolls are gone. The tools keep no history,
 * so nothing here renders saved scenes, taverns, companies or summons.
 *
 * Inside Satchel, three sub-modes share the same surface:
 *
 *   scene    Roll-a-Scene CTA + Saved scenes list. Opens the existing
 *            scene modal as an overlay; user stays in Your Hall.
 *   tavern   Roll-a-Tavern CTA + Saved taverns list.
 *   company  Pick-a-party UI + Saved companies list. Picking a party
 *            primes __hohPendingPartyRun (exact:true) and opens the
 *            company modal — picking a scenario inside the modal
 *            fires generation immediately (size = party member count).
 *
 * The active sub-mode persists in localStorage's saved.meta.
 * lastSatchelMode so a return visit lands where the user left off.
 */
(function(){
  'use strict';

  function esc(s){
    if(s == null) return '';
    return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#39;');
  }

  function characterById(id){
    var pool = (typeof window.hohGetAllCharacters === 'function') ? (window.hohGetAllCharacters() || []) : [];
    for(var i=0;i<pool.length;i++){ if(pool[i] && pool[i].id === id) return pool[i]; }
    return null;
  }

  function portraitStyle(c){
    return c && typeof window.hohImgStyle === 'function' ? window.hohImgStyle(c.id, 'primary') : '';
  }

  /* Re-render after the character pool resolves so portraits show up. */
  document.addEventListener('hoh:characters-loaded', function(){
    var host = document.getElementById('hoh-yh-tab-content');
    if(!host) return;
    /* Re-paint the Satchel surface in place when the pool arrives so
       tavern/scene patrons resolve.
       R439 LATENT BUG, caught while moving the Summon tile. The DM Tools
       drawer's root is class="hoh-satchel-shell hoh-tools-drawer" and it sits
       inside #hoh-yh-tab-content, so this matched it — and hohHallRenderSatchel
       writes host.innerHTML, which would have replaced the ENTIRE Profile tab
       (cards, filters, grid and all) with the old Satchel the moment the
       character pool landed late. It needs a real Satchel, not any shell. */
    var _shell = host.querySelector('.hoh-satchel-shell:not(.hoh-tools-drawer)');
    if(_shell){
      window.hohHallRenderSatchel(host);
    }
  });

  /* ================================================================ */
  /* Satchel tab (v2.3)                                               */
  /* ================================================================ */
  /* Mirrors the main hall's DM Satchel layout: Summon NPC bar,
     three feature tiles (Scene / Tavern / Company), Dice + Coin
     utility row. Below the tools, ONE unified saved list combines
     scenes, taverns, and companies sorted newest-first. Each tool
     auto-saves its result; the user prunes the list by removing
     unwanted entries. No sub-tabs, no toggles, no buttons that
     duplicate functionality. */

  /* The tool block — copied from the main hall pouch markup so the visual
     treatment is identical. SVG icons and text mirror the home-page Satchel
     exactly. R270: split into three named pieces (Summon feature bar, the
     Scene/Tavern/Company generate tiles, and the Dice/Coin tools row) so the
     Satchel can slot a clean section label between each group. Every tool id
     (summon/scene/tavern/company/dice/coin) is preserved exactly once. */
  function satchelSummonBarHtml(){
    return ''+
      '<button type="button" class="hoh-pouch-summon-bar" id="hoh-satchel-summon">'+
        '<div class="hoh-pouch-summon-left">'+
          '<div class="hoh-pouch-summon-icon">'+
            '<svg viewBox="0 0 48 48" width="40" height="40" fill="none" aria-hidden="true">'+
              '<defs><radialGradient id="hohSummonPortalGradV2" cx="50%" cy="45%" r="55%">'+
                '<stop offset="0%" stop-color="#c4a8f8" stop-opacity="0.8"/>'+
                '<stop offset="55%" stop-color="#8b6fd1" stop-opacity="0.35"/>'+
                '<stop offset="100%" stop-color="#6d4fa8" stop-opacity="0"/>'+
              '</radialGradient></defs>'+
              '<ellipse cx="24" cy="22" rx="17" ry="19" fill="url(#hohSummonPortalGradV2)"/>'+
              '<ellipse cx="24" cy="22" rx="15.5" ry="17.5" stroke="currentColor" stroke-width="1" fill="none" opacity="0.45"/>'+
              '<path d="M15 17 L24 3 L33 17 Z" stroke="currentColor" stroke-width="1.6" fill="rgba(164,216,255,0.22)" stroke-linejoin="round"/>'+
              '<path d="M10.5 17 Q24 13 37.5 17 Q24 19.5 10.5 17 Z" stroke="currentColor" stroke-width="1.6" fill="rgba(164,216,255,0.28)" stroke-linejoin="round"/>'+
              '<path d="M24 8 L25.2 10 L24 12 L22.8 10 Z" fill="currentColor" opacity="0.85"/>'+
              '<circle cx="24" cy="23" r="4" stroke="currentColor" stroke-width="1.5" fill="rgba(164,216,255,0.14)"/>'+
              '<path d="M12 42 Q12 30 24 30 Q36 30 36 42" stroke="currentColor" stroke-width="1.6" fill="rgba(164,216,255,0.1)" stroke-linejoin="round"/>'+
            '</svg>'+
          '</div>'+
          '<div class="hoh-pouch-summon-text">'+
            '<span class="hoh-pouch-summon-title">Summon a Hero NPC</span>'+
            '<span class="hoh-pouch-summon-sub">Pull a real hero from the Hall straight into your next session.</span>'+
          '</div>'+
        '</div>'+
        '<span class="hoh-pouch-summon-arrow" aria-hidden="true">&rarr;</span>'+
      '</button>';
  }
  function satchelHeroTilesHtml(){
    /* R439 (Jan): "instead of summon a hero npc as its own top module, make
       there just be 3 modules ... it will just be Summon, Scene, and Tavern."
       Summon was a full width banner above three tiles, which said it was a
       different KIND of thing when it is the same thing: press it, get
       something for your session. As a tile it reads as one of three, and the
       row stops being a banner plus a leftover.
       Company is deleted rather than moved. It was pointer-events:none behind
       a "work in progress" chip, so it spent a third of the row telling people
       about something they cannot use. openCompanySourcePicker is left in
       place, unreferenced, in case it ships later. */
    return ''+
      '<div class="hoh-pouch-heroes">'+
        '<div class="hoh-pouch-hero hoh-pouch-hero-summon" id="hoh-satchel-summon" role="button" tabindex="0">'+
          '<div class="hoh-pouch-hero-icon">'+
            '<svg viewBox="0 0 48 48" width="42" height="42" fill="none" aria-hidden="true">'+
              '<defs><radialGradient id="hohSummonPortalGradV3" cx="50%" cy="45%" r="55%">'+
                '<stop offset="0%" stop-color="#c4a8f8" stop-opacity="0.8"/>'+
                '<stop offset="55%" stop-color="#8b6fd1" stop-opacity="0.35"/>'+
                '<stop offset="100%" stop-color="#6d4fa8" stop-opacity="0"/>'+
              '</radialGradient></defs>'+
              '<ellipse cx="24" cy="22" rx="17" ry="19" fill="url(#hohSummonPortalGradV3)"/>'+
              '<ellipse cx="24" cy="22" rx="15.5" ry="17.5" stroke="#a4d8ff" stroke-width="1" fill="none" opacity="0.45"/>'+
              '<path d="M15 17 L24 3 L33 17 Z" stroke="#a4d8ff" stroke-width="1.6" fill="rgba(164,216,255,0.22)" stroke-linejoin="round"/>'+
              '<path d="M10.5 17 Q24 13 37.5 17 Q24 19.5 10.5 17 Z" stroke="#a4d8ff" stroke-width="1.6" fill="rgba(164,216,255,0.28)" stroke-linejoin="round"/>'+
              '<circle cx="24" cy="23" r="4" stroke="#a4d8ff" stroke-width="1.5" fill="rgba(164,216,255,0.14)"/>'+
              '<path d="M12 42 Q12 30 24 30 Q36 30 36 42" stroke="#a4d8ff" stroke-width="1.6" fill="rgba(164,216,255,0.1)" stroke-linejoin="round"/>'+
            '</svg>'+
          '</div>'+
          '<div class="hoh-pouch-hero-name">Summon</div>'+
          '<div class="hoh-pouch-hero-sub">Pull a real hero from the Hall into your session.</div>'+
        '</div>'+
        '<div class="hoh-pouch-hero hoh-pouch-hero-scene" id="hoh-satchel-scene" role="button" tabindex="0">'+
          '<div class="hoh-pouch-hero-icon">'+
            '<svg viewBox="0 0 48 48" width="42" height="42" fill="none">'+
              '<rect x="9" y="7" width="30" height="34" rx="3" stroke="#7ed49a" stroke-width="1.6" fill="rgba(126,212,154,0.08)"/>'+
              '<line x1="15" y1="17" x2="33" y2="17" stroke="#7ed49a" stroke-width="1.4" stroke-linecap="round"/>'+
              '<line x1="15" y1="24" x2="29" y2="24" stroke="#7ed49a" stroke-width="1.4" stroke-linecap="round"/>'+
              '<line x1="15" y1="31" x2="31" y2="31" stroke="#7ed49a" stroke-width="1.4" stroke-linecap="round"/>'+
            '</svg>'+
          '</div>'+
          '<div class="hoh-pouch-hero-name">Scene</div>'+
          '<div class="hoh-pouch-hero-sub">Who walks in. What they want. What\'s wrong.</div>'+
        '</div>'+
        '<div class="hoh-pouch-hero hoh-pouch-hero-tavern" id="hoh-satchel-tavern" role="button" tabindex="0">'+
          '<div class="hoh-pouch-hero-icon">'+
            '<svg viewBox="0 0 48 48" width="42" height="42" fill="none">'+
              '<path d="M 20 9 Q 22 7 20 5 M 26 10 Q 28 8 26 6" stroke="#e2c67a" stroke-width="1" stroke-linecap="round" opacity="0.4" fill="none"/>'+
              '<rect x="13" y="14" width="18" height="26" rx="2" stroke="#e2c67a" stroke-width="1.6" fill="rgba(226,198,122,0.08)"/>'+
              '<rect x="13" y="14" width="18" height="5" rx="1" stroke="#e2c67a" stroke-width="1" fill="rgba(226,198,122,0.18)"/>'+
              '<path d="M 31 20 Q 39 20 39 28 Q 39 34 31 34" stroke="#e2c67a" stroke-width="1.6" fill="none"/>'+
              '<line x1="17" y1="22" x2="17" y2="37" stroke="#e2c67a" stroke-width="0.8" opacity="0.5"/>'+
              '<line x1="22" y1="22" x2="22" y2="37" stroke="#e2c67a" stroke-width="0.8" opacity="0.5"/>'+
              '<line x1="27" y1="22" x2="27" y2="37" stroke="#e2c67a" stroke-width="0.8" opacity="0.5"/>'+
            '</svg>'+
          '</div>'+
          '<div class="hoh-pouch-hero-name">Tavern</div>'+
          '<div class="hoh-pouch-hero-sub">Set the room before they walk in.</div>'+
        '</div>'+
      '</div>';
  }
  function satchelUtilsHtml(){
    return ''+
      '<div class="hoh-pouch-utils">'+
        '<button type="button" class="hoh-pouch-util" id="hoh-satchel-dice">'+
          '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" style="flex-shrink:0;">'+
            '<path d="M12 3 L21 7.5 L21 16.5 L12 21 L3 16.5 L3 7.5 Z M3 7.5 L12 12 L21 7.5 M12 12 L12 21" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>'+
            '<circle cx="7.5" cy="11" r="0.9" fill="currentColor"/>'+
            '<circle cx="16.5" cy="11" r="0.9" fill="currentColor"/>'+
            '<circle cx="12" cy="16.5" r="0.9" fill="currentColor"/>'+
          '</svg>'+
          '<span class="hoh-pouch-util-label">Dice</span>'+
          '<span class="hoh-pouch-util-sub">d4&ndash;d100</span>'+
        '</button>'+
        '<button type="button" class="hoh-pouch-util" id="hoh-satchel-coin">'+
          '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" style="flex-shrink:0;">'+
            '<circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.6" fill="none"/>'+
          '</svg>'+
          '<span class="hoh-pouch-util-label">Coin</span>'+
          '<span class="hoh-pouch-util-sub">Flip it</span>'+
        '</button>'+
      '</div>';
  }

  /* ------------------------------------------------------------------ */
  /* Satchel · Company source picker                                    */
  /* ------------------------------------------------------------------ */
  /* Why this exists:
     The Company tile in My Party is unambiguous — the party panel
     already defines which heroes fill the seats, and Run This Party
     just pipes those IDs straight into the company flow with
     exact:true.

     The Company tile in the Satchel is fundamentally different. The
     user hasn't pre-selected a roster, so we owe them a choice:

       1. Run a saved party  → exact:true, fixed memberIds
       2. From My Favorites  → pool filter, size picker decides cut
       3. From My Hearted    → pool filter, size picker decides cut
       4. From All Heroes    → no filter, standard top-80 query

     The picker is a small modal we mount on document.body so the
     hall-mode CSS doesn't hide it. After a pick, we set
     __hohPendingPartyRun appropriately and call hohOpenCompanyTool. */

  function getHeartedIds(){
    /* Read hoh-hearts directly. The map shape is { id: count }; we
       want any id whose count > 0. Fail soft to []. */
    try {
      var raw = localStorage.getItem('hoh-hearts');
      if(!raw) return [];
      var v = JSON.parse(raw);
      if(Array.isArray(v)) return v.slice();
      if(v && typeof v === 'object'){
        return Object.keys(v).filter(function(id){ return (v[id] || 0) > 0; });
      }
      return [];
    } catch(_){ return []; }
  }

  function partyChooserCardHtml(party){
    /* Compact party card for the chooser. Shows the party name +
       color stripe + filled slot count. We only allow parties with
       at least 2 filled slots (Company minimum).

       Important: party.slots in the data layer is a flat array where
       each slot is either a hero id STRING or null — NOT an object
       with a `.characterId` field. Earlier code assumed an object
       shape and read 0 hero counts on real parties. The picker now
       reads the slot values directly. */
    var slotIds = (party.slots || []).filter(function(s){ return !!s; });
    var filled = slotIds.length;
    var disabled = filled < 2;
    var color = party.color || '#c8a45e';
    var name = esc(party.name || 'Untitled Party');
    /* Tiny portrait strip, max 6 thumbs. */
    var thumbs = slotIds.slice(0, 6).map(function(id){
      var c = characterById(id);
      var img = c && (c.image_thumb_url || c.image_url);
      if(img){
        return '<span class="hoh-csp-party-thumb"><img src="' + esc(img) + '" alt="" style="' + esc(portraitStyle(c)) + '" loading="lazy" decoding="async" crossorigin="anonymous"></span>';
      }
      return '<span class="hoh-csp-party-thumb hoh-csp-party-thumb-empty"></span>';
    }).join('');
    return '<button type="button" class="hoh-csp-party"' +
      (disabled ? ' disabled' : '') +
      ' data-party-id="' + esc(party.id) + '" style="--csp-color:' + esc(color) + ';">' +
      '<span class="hoh-csp-party-stripe"></span>' +
      '<span class="hoh-csp-party-body">' +
        '<span class="hoh-csp-party-name">' + name + '</span>' +
        '<span class="hoh-csp-party-meta">' + filled + ' hero' + (filled === 1 ? '' : 'es') +
          (disabled ? ' · need 2+ to run' : '') +
        '</span>' +
        (thumbs ? '<span class="hoh-csp-party-thumbs">' + thumbs + '</span>' : '') +
      '</span>' +
    '</button>';
  }

  function closeCompanySourcePicker(){
    var el = document.getElementById('hoh-company-source-picker');
    if(el && el.parentNode) el.parentNode.removeChild(el);
  }

  function fireCompanyTool(){
    /* Small wrap so the picker can close before the company modal
       takes over body scroll. */
    closeCompanySourcePicker();
    if(typeof window.hohOpenCompanyTool === 'function') window.hohOpenCompanyTool();
  }

  function openCompanySourcePicker(){
    /* Tear down any prior instance — repeated taps shouldn't stack. */
    closeCompanySourcePicker();

    var hall = (typeof window.hohHallGet === 'function') ? window.hohHallGet() : { heroes: [], parties: [] };
    var parties = hall.parties || [];

    /* R63: Pool-pick path removed entirely. Company is now strictly
       run on a saved party (so the AI output is about THIS group, not
       a random pull). If the user has zero runnable parties, surface
       a beautiful empty state CTA — Build Your First Party. The
       runnable threshold is 2 filled slots (Company minimum). */
    var runnableParties = parties.filter(function(p){
      var slotIds = (p.slots || []).filter(function(s){ return !!s; });
      return slotIds.length >= 2;
    });

    var bodyHtml;
    if(runnableParties.length){
      bodyHtml =
        '<div class="hoh-csp-section">' +
          '<div class="hoh-csp-section-title">Run a saved party</div>' +
          '<div class="hoh-csp-section-sub">Your party becomes the company. The seats are already filled by your characters.</div>' +
          '<div class="hoh-csp-parties">' + parties.map(partyChooserCardHtml).join('') + '</div>' +
        '</div>';
    } else {
      /* No runnable parties — empty state with a one-tap CTA into
         My Party. Even users with a half-built party (1 hero) end
         up here because Company needs >=2. */
      var hasHalfBuilt = parties.length > 0;
      bodyHtml =
        '<div class="hoh-csp-section hoh-csp-empty">' +
          '<div class="hoh-csp-empty-icon" aria-hidden="true">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="8" r="3"/><path d="M3 19a6 6 0 0 1 12 0"/><circle cx="17" cy="8" r="2.5"/><path d="M14.5 19a4.5 4.5 0 0 1 7-3.7"/></svg>' +
          '</div>' +
          '<div class="hoh-csp-empty-title">' + (hasHalfBuilt ? 'Add a few more heroes to your party first.' : 'Build a party first.') + '</div>' +
          '<div class="hoh-csp-empty-body">' +
            'Company runs on YOUR party — the heroes you (or your friends) made, in the lineup you chose. ' +
            (hasHalfBuilt
              ? 'Drop one more hero into a slot and we can read the dynamic.'
              : 'Head to My Party, drag heroes into the slots, and come back. Two heroes minimum.') +
          '</div>' +
          '<button type="button" class="hoh-csp-empty-cta" data-csp-go-party="1">' +
            (hasHalfBuilt ? 'Open My Party' : 'Build a party') +
          '</button>' +
        '</div>';
    }

    var modal = document.createElement('div');
    modal.id = 'hoh-company-source-picker';
    modal.className = 'hoh-csp-modal';
    modal.innerHTML =
      '<div class="hoh-csp-backdrop" data-csp-close="1"></div>' +
      '<div class="hoh-csp-shell" role="dialog" aria-modal="true" aria-labelledby="hoh-csp-title">' +
        '<button type="button" class="hoh-csp-close" data-csp-close="1" aria-label="Close">&times;</button>' +
        '<div class="hoh-csp-head">' +
          '<div class="hoh-csp-eyebrow">The Satchel</div>' +
          '<div class="hoh-csp-title" id="hoh-csp-title">Pick a Party</div>' +
          '<div class="hoh-csp-sub">' + (runnableParties.length ? 'Then you\'ll pick a scenario for them.' : '') + '</div>' +
        '</div>' +
        bodyHtml +
      '</div>';
    document.body.appendChild(modal);
    /* Lock background scroll while picker is up. */
    var prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    modal.__hohPrevOverflow = prevOverflow;

    /* Wire all clicks at the modal level — backdrop close, party
       cards, empty-state CTA. */
    modal.addEventListener('click', function(e){
      var t = e.target;
      if(!t) return;
      if(t.closest('[data-csp-close]')){
        e.preventDefault();
        document.body.style.overflow = modal.__hohPrevOverflow || '';
        closeCompanySourcePicker();
        return;
      }
      /* Empty-state CTA — close picker and take the user to the LIVE party
         builder. BUG-UX3 (2026-08-06): this used to switch to 'party', which
         the view switcher aliases to the Satchel, and the Satchel has not
         held a party builder since R302 — so the CTA walked the user in a
         circle with no exit. The live builder is the Party builder pane in
         the Shop collection; open the Shop tab and put the collection in
         builder mode. */
      if(t.closest('[data-csp-go-party]')){
        e.preventDefault();
        document.body.style.overflow = modal.__hohPrevOverflow || '';
        closeCompanySourcePicker();
        if(typeof window.hohShopSetCollView === 'function'){
          try { window.hohShopSetCollView('party'); } catch(_){}
        }
        if(typeof window.hohYourHallSwitchView === 'function'){
          try { window.hohYourHallSwitchView('shop'); } catch(_){}
        } else if(typeof window.hohOpenYourHall === 'function'){
          try { window.hohOpenYourHall('shop'); } catch(_){}
        }
        /* The Shop paints after an async state fetch; nudge the view to the
           pane once it exists. Guarded — if it is not there yet the Shop
           still opens in builder mode, which is the destination that counts. */
        setTimeout(function(){
          try {
            var pane = document.getElementById('ffshopPartyPane');
            if(pane && pane.scrollIntoView) pane.scrollIntoView({ behavior:'smooth', block:'center' });
          } catch(_){}
        }, 600);
        return;
      }
      var partyBtn = t.closest('.hoh-csp-party');
      if(partyBtn && !partyBtn.disabled){
        e.preventDefault();
        var pid = partyBtn.getAttribute('data-party-id');
        var party = (parties || []).find(function(p){ return p.id === pid; });
        if(!party) return;
        /* Slots are hero id strings (or null), not objects. Filter
           out nulls and pass the ids through directly. */
        var memberIds = (party.slots || []).filter(Boolean);
        if(memberIds.length < 2) return;
        window.__hohPendingPartyRun = { memberIds: memberIds, exact: true, partyId: pid, partyName: party.name, partyColor: party.color };
        document.body.style.overflow = modal.__hohPrevOverflow || '';
        fireCompanyTool();
        return;
      }
    });

    /* Esc closes. */
    function onKey(e){
      if(e.key === 'Escape'){
        document.removeEventListener('keydown', onKey);
        document.body.style.overflow = modal.__hohPrevOverflow || '';
        closeCompanySourcePicker();
      }
    }
    document.addEventListener('keydown', onKey);
  }

  /* R268: collapsible section state (Party, Saved Rolls). The last open/closed
     choice is remembered per section in localStorage so a return visit reopens
     the way the user left it. Both default OPEN on a fresh account. */
  var SATCHEL_COLLAPSE_KEY = 'hoh-satchel-collapse-v1';
  function collapseState(){ try{ return JSON.parse(localStorage.getItem(SATCHEL_COLLAPSE_KEY)||'{}')||{}; }catch(_){ return {}; } }
  /* R440: the default must not depend on a WRITE succeeding.
     Every one of these helpers is wrapped in try/catch around localStorage, so
     on a browser with storage blocked (private mode on some engines, a locked
     down profile, an embedded webview) setCollapsed silently does nothing and
     `=== true` then reports NOT collapsed — the exact opposite of the intended
     default. Reading a per-section default when no choice has been stored
     makes "closed unless you opened it" true whether or not storage works. */
  function isCollapsed(key, defaultCollapsed){
    var st = collapseState();
    return st[key] === undefined ? !!defaultCollapsed : st[key] === true;
  }
  function setCollapsed(key, val){
    try{ var s=collapseState(); s[key]=!!val; localStorage.setItem(SATCHEL_COLLAPSE_KEY, JSON.stringify(s)); }catch(_){}
  }
  function chevronSvg(){
    return '<svg class="hoh-satchel-chevron" viewBox="0 0 24 24" width="14" height="14" fill="none" aria-hidden="true">' +
      '<path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  }
  /* One collapsible section: a header button (label + chevron) that toggles a
     body. `key` persists the state; `label` is the header text; `bodyHtml` is
     the section content; `bodyId` (optional) is set on the body wrapper. */
  function collapseSectionHtml(key, label, bodyHtml, bodyId, defaultCollapsed){
    var collapsed = isCollapsed(key, defaultCollapsed);
    return '<div class="hoh-satchel-collapse'+(collapsed?' is-collapsed':'')+'" data-collapse="'+esc(key)+'">' +
      '<button type="button" class="hoh-satchel-collapse-head" aria-expanded="'+(collapsed?'false':'true')+'">' +
        '<span class="hoh-satchel-collapse-label">'+esc(label)+'</span>' + chevronSvg() +
      '</button>' +
      '<div class="hoh-satchel-collapse-body"'+(bodyId?(' id="'+esc(bodyId)+'"'):'')+'>' + bodyHtml + '</div>' +
    '</div>';
  }
  /* A plain (non-collapsible) section label — shares the visual family of the
     collapse header label (Cinzel, letterspaced, trailing hairline rule) so
     every block in the Satchel reads as one system. Used for the always-open
     areas (Collection, Generate, Tools) that don't need a toggle. */
  function sectionLabelHtml(label){
    return '<div class="hoh-satchel-section-label"><span>'+esc(label)+'</span></div>';
  }

  /* The main Satchel entry point.
     R270 layout: everything lives inside ONE master collapsible titled
     "The DM's Satchel" (key 'all', default OPEN) so the whole panel can be
     minimized to just its header. Inside the master body, top to bottom:
       • "Your Collection" label + the #hoh-satchel-collection mount
       • "Summon" label + the prominent Summon-a-Hero feature bar
       • "Generate" label + the Scene / Tavern / Company tiles
       • "Tools" label + the Dice / Coin row
       • "Your Party" collapsible (key 'party')
       • "Saved Rolls" collapsible (key 'saved')
     Every id and mount point is preserved. The sub-section heads (party/saved)
     live inside the master BODY — never its head — so the delegated toggle's
     `closest('.hoh-satchel-collapse')` targets the clicked section only:
     clicking a sub-head toggles that sub-section, clicking the master head
     toggles the whole panel. Each tile launches the matching tool modal
     (body-rehomed by hoh-hall-data.js). */
  window.hohHallRenderSatchel = function(host){
    if(!host) return;
    /* R258 layout (Jan's spec): the DM's Satchel is a FIXED masthead — the
       label of the whole tab, never minimizable. Under it exactly THREE
       pockets, each with its own color identity:
         [1] "Your Collection" (gold)   — collapsible, the one and only label
         [2] "Your Party"      (pink)   — static under-construction placeholder
             while the party mechanics are rebuilt (hoh-hall-party.js dormant)
         [3] "Tools"           (blue)   — ONE collapsible holding Summon +
             Generate + Dice/Coin + Saved Rolls together
       Every mount id is preserved (#hoh-satchel-collection)
       so refreshers keep working; #hoh-satchel-party is intentionally gone. */
    if(!document.getElementById('hoh-satchel-r258-css')){
      var st = document.createElement('style'); st.id = 'hoh-satchel-r258-css';
      st.textContent =
        '.hoh-satchel-masthead{display:flex;align-items:center;gap:14px;margin:4px 0 14px;'+
          'font-family:\'Cinzel\',serif;font-weight:700;font-size:16px;letter-spacing:.2em;'+
          'text-transform:uppercase;color:#f2d98e}'+
        '.hoh-satchel-masthead::after{content:"";height:1px;flex:1;'+
          'background:linear-gradient(90deg,rgba(200,164,94,.6),transparent)}'+
        '.hoh-satchel-collapse[data-collapse="toolkit"] .hoh-satchel-collapse-label{color:#8fd8ff}'+
        '.hoh-satchel-collapse[data-collapse="toolkit"]>.hoh-satchel-collapse-head{border-color:rgba(126,212,255,.35)}'+
        '.hoh-satchel-party-slot{margin:10px 0}'+
        '.hoh-satchel-static-head{display:flex;align-items:center;gap:12px;width:100%;'+
          'padding:10px 2px;border-bottom:1px solid rgba(239,148,255,.3)}'+
        '.hoh-satchel-static-head .hoh-satchel-collapse-label{color:#e7b4f5}'+
        '.hoh-satchel-soon-chip{font-family:\'Cinzel\',serif;font-size:8.5px;font-weight:600;'+
          'letter-spacing:.18em;text-transform:uppercase;color:#e7b4f5;'+
          'border:1px dashed rgba(239,148,255,.55);border-radius:999px;padding:4px 10px}'+
        '.hoh-satchel-soon-sub{font-family:\'Cormorant Garamond\',Georgia,serif;font-style:italic;'+
          'font-size:14px;color:#a09aae;margin:10px 2px 4px;line-height:1.45}'+
        /* R260: air between the toolkit blocks — summon bar, generator
           tiles, dice/coin row and Saved Rolls read as separate shelves,
           never flush. */
        '.hoh-satchel-collapse[data-collapse="toolkit"] .hoh-satchel-collapse-body>*{margin-bottom:26px}'+
        '.hoh-satchel-collapse[data-collapse="toolkit"] .hoh-satchel-collapse-body>*:last-child{margin-bottom:8px}'+
        '.hoh-satchel-collapse[data-collapse="toolkit"] #hoh-satchel-saved{margin-top:30px;padding-top:22px;'+
          'border-top:1px solid rgba(126,212,255,.16)}'+
        /* R260: the Party gate — locked until 4 collected cards. */
        '.hoh-satchel-party-locked{display:flex;flex-direction:column;align-items:center;gap:8px;'+
          'padding:18px 14px 20px;text-align:center}'+
        '.hoh-satchel-party-locked .lock{font-size:20px;color:#e7b4f5;opacity:.9}'+
        '.hoh-satchel-party-locked b{font-family:\'Cinzel\',serif;font-size:12.5px;letter-spacing:.14em;'+
          'text-transform:uppercase;color:#e7b4f5}'+
        '.hoh-satchel-party-locked i{font-family:\'Cormorant Garamond\',Georgia,serif;font-style:italic;'+
          'font-size:14.5px;color:#a09aae;line-height:1.5;max-width:420px}';
      (document.head||document.documentElement).appendChild(st);
    }
    host.innerHTML =
      '<div class="hoh-satchel-shell">' +
        '<div class="hoh-satchel-masthead"><span>The DM&rsquo;s Satchel</span></div>' +
        collapseSectionHtml('collection', 'Your Collection',
          '<div id="hoh-satchel-collection" class="hoh-satchel-collection"></div>') +
        /* R302: the Party pocket is retired (Jan's call) — the party mechanics
           are being rebuilt around the Battlegrounds, so the satchel is
           Collection + Tools until they return. */
        collapseSectionHtml('toolkit', 'Tools',
          satchelHeroTilesHtml() +
          satchelUtilsHtml()) +
      '</div>';
    /* R302: the party pocket, its 4-card gate and the builder mount are gone.
       hoh-shop still calls hohSatchelSyncPartyGate after a collection paint,
       so keep a no-op on the window rather than leaving a dangling call. */
    window.hohSatchelSyncPartyGate = function(){};
    /* R268: mount the card collection (hoh-shop.js owns the grid + state). */
    try {
      var _cc = host.querySelector('#hoh-satchel-collection');
      if(_cc && typeof window.hohShopRenderCollection === 'function') window.hohShopRenderCollection(_cc);
    } catch(_){}

    /* Wire host-level click handlers ONCE. The tile click handlers
       just delegate to the existing window.hohOpen* tool functions
       — the tools themselves haven't changed. */
    if(!host.__hohSatchelWired){
      host.__hohSatchelWired = true;
      host.addEventListener('click', function(e){
        var t = e.target;
        if(!t) return;
        /* R268: collapse toggle — remember the new state and flip the section. */
        var head = t.closest('.hoh-satchel-collapse-head');
        if(head){
          e.preventDefault();
          var sec = head.closest('.hoh-satchel-collapse');
          if(sec){
            var key = sec.getAttribute('data-collapse');
            var nowCollapsed = !sec.classList.contains('is-collapsed');
            sec.classList.toggle('is-collapsed', nowCollapsed);
            head.setAttribute('aria-expanded', nowCollapsed ? 'false' : 'true');
            setCollapsed(key, nowCollapsed);
          }
          return;
        }
        if(t.closest('#hoh-satchel-summon')){ e.preventDefault(); if(typeof window.hohSummonRandomNpc === 'function') window.hohSummonRandomNpc(); return; }
        if(t.closest('#hoh-satchel-scene')){ e.preventDefault(); if(typeof window.hohOpenSceneTool === 'function') window.hohOpenSceneTool(); return; }
        if(t.closest('#hoh-satchel-tavern')){ e.preventDefault(); if(typeof window.hohOpenTavernTool === 'function') window.hohOpenTavernTool(); return; }
        if(t.closest('#hoh-satchel-company')){ e.preventDefault(); openCompanySourcePicker(); return; }
        if(t.closest('#hoh-satchel-dice')){ e.preventDefault(); if(typeof window.hohOpenDiceTool === 'function') window.hohOpenDiceTool(); return; }
        if(t.closest('#hoh-satchel-coin')){ e.preventDefault(); if(typeof window.hohOpenCoinTool === 'function') window.hohOpenCoinTool(); return; }
      });
    }
  };

  /* ============================================================
     R380 (2026-08-06, Jan): THE TOOLS DRAWER.
     The Satchel tab is gone. The DM tools and the saved rolls live in
     ONE collapsible drawer at the bottom of the Profile tab, closed by
     default. Same tiles, same handlers, same saved feed, no collection
     (the Profile grid above it IS the collection now).
     Jan (2026-09-27): no saved feed; the tools just run. opts.bare is the
     hub's DM Tools door (hoh-your-hall.js): its row is the header and its
     line says what the tools are for, so it takes the tiles and Dice/Coin
     only, with no collapse header and no note.
     ============================================================ */
  window.hohHallRenderToolsPanel = function(host, opts){
    if(!host) return;
    var bare = !!(opts && opts.bare);
    /* R439 (Jan): "under dm tools it should come not expanded they need to
       manually expand it."
       It already seeded closed, but only ON A BROWSER THAT HAD NEVER SEEN IT
       — `=== undefined` means anyone who ever opened it has it open forever,
       which is everyone who has used the tab. Bumping the key retires those
       stored choices once and lets the new default land, and the toggle still
       remembers from here on. */
    /* R439/R440: closed unless the player has opened it. The key was bumped
       once so everyone who had the old drawer open gets the new default; from
       here the toggle remembers. The `true` in the collapseSectionHtml call
       below is what actually decides it, so no storage write is needed to make
       the default hold. */
    var key = 'tools-drawer-v2';
    host.innerHTML = bare ?
      '<div class="hoh-tools-drawer hoh-tools-bare">' + satchelHeroTilesHtml() + satchelUtilsHtml() + '</div>' :
      '<div class="hoh-satchel-shell hoh-tools-drawer">' +
        collapseSectionHtml(key, 'DM Tools',
          /* R439: "under it it should just be like these are just for fun,
             they dont affect the battlegrounds or cards in any, just tools i
             built to utilize heroes in the hall." Sitting above the tiles
             rather than beside the header, so it is read on the way in
             instead of ignored on the way past.
             Jan (2026-09-24): replace it with something more useful, "like more
             ways to use the 1000+ characters added by other players". */
          '<p class="hoh-tools-note">More ways to use the 1,000+ heroes other players have added to the Hall. ' +
            'Summon one as an NPC, set a scene or fill a tavern for your next session.</p>' +
          satchelHeroTilesHtml() +
          satchelUtilsHtml(), null, true) +
      '</div>';
    if(!host.__hohSatchelWired){
      host.__hohSatchelWired = true;
      host.addEventListener('click', function(e){
        var t = e.target;
        if(!t) return;
        var head = t.closest('.hoh-satchel-collapse-head');
        if(head){
          e.preventDefault();
          var sec = head.closest('.hoh-satchel-collapse');
          if(sec){
            var k = sec.getAttribute('data-collapse');
            var nowCollapsed = !sec.classList.contains('is-collapsed');
            sec.classList.toggle('is-collapsed', nowCollapsed);
            head.setAttribute('aria-expanded', nowCollapsed ? 'false' : 'true');
            setCollapsed(k, nowCollapsed);
          }
          return;
        }
        if(t.closest('#hoh-satchel-summon')){ e.preventDefault(); if(typeof window.hohSummonRandomNpc === 'function') window.hohSummonRandomNpc(); return; }
        if(t.closest('#hoh-satchel-scene')){ e.preventDefault(); if(typeof window.hohOpenSceneTool === 'function') window.hohOpenSceneTool(); return; }
        if(t.closest('#hoh-satchel-tavern')){ e.preventDefault(); if(typeof window.hohOpenTavernTool === 'function') window.hohOpenTavernTool(); return; }
        if(t.closest('#hoh-satchel-company')){ e.preventDefault(); openCompanySourcePicker(); return; }
        if(t.closest('#hoh-satchel-dice')){ e.preventDefault(); if(typeof window.hohOpenDiceTool === 'function') window.hohOpenDiceTool(); return; }
        if(t.closest('#hoh-satchel-coin')){ e.preventDefault(); if(typeof window.hohOpenCoinTool === 'function') window.hohOpenCoinTool(); return; }
      });
      /* R440: the three tiles are divs, so Enter and Space do nothing on their
         own. They carry role="button" and tabindex="0" now; this turns those
         keys into the click the handler above already understands. Dice and
         Coin are real buttons and need none of this. */
      host.addEventListener('keydown', function(e){
        if(e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
        var t = e.target;
        if(!t || !t.closest) return;
        var tile = t.closest('.hoh-pouch-hero[role="button"]');
        if(!tile) return;
        e.preventDefault();
        tile.click();
      });
    }
  };

  /* ------------------------------------------------------------------ */
  /* Shared toast (used by all hall save actions)                       */
  /* ------------------------------------------------------------------ */

  var TOAST_TIMER = null;
  window.hohShowHallToast = function(text, opts){
    var existing = document.getElementById('hoh-hall-toast');
    if(existing){ existing.parentNode.removeChild(existing); }
    var el = document.createElement('div');
    el.id = 'hoh-hall-toast';
    el.className = 'hoh-hall-toast';
    var safe = esc(text || '');
    var linkHtml = '';
    if(opts && opts.linkLabel){
      var href = opts.linkHref || '#';
      linkHtml = ' <a href="' + esc(href) + '"' + (opts.linkClick ? ' data-toast-action="1"' : '') + '>' + esc(opts.linkLabel) + '</a>';
    }
    el.innerHTML = safe + linkHtml;
    document.body.appendChild(el);
    /* Animate in on next frame. */
    requestAnimationFrame(function(){ el.classList.add('hoh-hall-toast-on'); });
    if(opts && opts.linkClick){
      var a = el.querySelector('[data-toast-action]');
      if(a) a.addEventListener('click', function(e){ e.preventDefault(); try { opts.linkClick(); } catch(_){} hide(); });
    }
    function hide(){
      el.classList.remove('hoh-hall-toast-on');
      setTimeout(function(){ if(el.parentNode) el.parentNode.removeChild(el); }, 240);
    }
    clearTimeout(TOAST_TIMER);
    TOAST_TIMER = setTimeout(hide, (opts && opts.duration) || 2400);
  };
})();
